import { SentenceLevel } from '../../models/sentenceLevel';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef
} from '@angular/core';
import Offense from '../../models/offense';
import OffenseRecord from '../../models/offenseRecord';
import { FormGroup } from '@angular/forms';
import DateHandler from '../../models/dateHandler';
import FormControl from '../../models/formControl';
import PopupHelper from '../../models/popupHelper';
import { ConfirmResult } from '../../models/alertifyPromise';

@Component({
  selector: 'app-offense-record',
  templateUrl: './offenseRecord.component.html',
  styleUrls: [
    './offenseRecord.component.scss'
  ]
})
export default class OffenseRecordComponent {

  @Output() updateToCurrentOffenseRecord = new EventEmitter<OffenseRecord>();
  @Output() updateCanDeleteCurrentOffenseRecord = new EventEmitter<boolean>();
  @Output() updateToCurrentOffenseCharges = new EventEmitter();

  @Input() requestNumber: number;

  @Input() set offenseObject(offense: Offense) {
    if (offense) {
      this.offense = offense;
      if (offense.offenseRecords.length > 0) {
        this.firstOffense();
      } else {
        this.addNewRecord();
      }
    }
  }

  @Input() set offenseRecordFormGroupObject(offenseRecordFormGroup: FormGroup) {
    this.offenseRecordFormGroup = offenseRecordFormGroup;
    this.formControl = new FormControl(this.offenseRecordFormGroup);
  }

  get isFirstActive() {
    return this.firstButtonActive;
  }

  get isPreviousActive() {
    return this.previousButtonActive;
  }

  get isNextActive() {
    return this.nextButtonActive;
  }

  get isLastActive() {
    return this.lastButtonActive;
  }

  get addNewLastActive() {
    return this.addNewButtonActive;
  }

  public hasHeightenedIdentifier: Boolean;

// tslint:disable-next-line: use-life-cycle-interface
  ngOnChanges() {
      if (this.offense != null) { this.hasHeightenedIdentifier = this.offense.offenseSearch.isHeightenedIdentifier; }
    }

  get currentRecord(): string {
    let currentRecord: string;
    if (this.isNewRecord) {
      currentRecord = 'This is a new record that has not been saved.';
    } else if (this.currentOffenseRecordIndex === undefined) {
      currentRecord = '0';
    } else {
      currentRecord = (this.currentOffenseRecordIndex + 1).toString();
    }

    return currentRecord;
  }

  get totalRecords(): string {
    return this.hasOffenses ?
      (this.isNewRecord ? this.offense.offenseRecords.length - 1
      : this.offense.offenseRecords.length).toString() : '0';
  }

  formatDate(controlName: string, baseCentury: number) {
    if (!this.formControl.hasErrorByControlName(controlName)) {
      const value = this.formControl.getValueByControlName(controlName);
      this.formControl.setValueByControlName(controlName, this.convertToDateFormat(value, baseCentury));
    }

    this.validateDates();
  }

  async firstOffense(ignoreMinOffenceRecord: boolean = false, ignoreUnsavedChanges: boolean = false) {
    if ((!this.onMinOffenseRecord || ignoreMinOffenceRecord) && this.hasOffenses) {
      let save = true;
      if (!ignoreUnsavedChanges
          && this.offenseRecordFormGroup.dirty
          && await PopupHelper.saveUnsavedChangesHelper() === ConfirmResult.Cancel) {
        save = false;
      }

      if (save) {
        this.removeNewRecord();
        this.currentOffenseRecordIndex = 0;
        this.currentOffenseRecord = this.offense.offenseRecords[this.currentOffenseRecordIndex];
        this.updateFormControls();
        this.updateToCurrentOffenseRecord.emit(this.currentOffenseRecord);
        this.updateCanDeleteCurrentOffenseRecord.emit(true);
        this.offenseRecordFormGroup.markAsPristine();
      }
    }
  }

  async previousOffense() {
    if (!this.onMinOffenseRecord && this.hasOffenses) {
      let save = true;
      if (this.offenseRecordFormGroup.dirty && await PopupHelper.saveUnsavedChangesHelper() === ConfirmResult.Cancel) {
        save = false;
      }

      if (save) {
        this.removeNewRecord();
        this.currentOffenseRecordIndex--;
        this.currentOffenseRecord = this.offense.offenseRecords[this.currentOffenseRecordIndex];
        this.updateFormControls();
        this.updateToCurrentOffenseRecord.emit(this.currentOffenseRecord);
        this.updateCanDeleteCurrentOffenseRecord.emit(true);
        this.offenseRecordFormGroup.markAsPristine();
      }
    }
  }

  async nextOffense() {
    if (!this.onMaxOffenseRecord && this.hasOffenses) {
      let save = true;
      if (this.offenseRecordFormGroup.dirty && await PopupHelper.saveUnsavedChangesHelper() === ConfirmResult.Cancel) {
        save = false;
      }

      if (save) {
        this.removeNewRecord();
        this.currentOffenseRecordIndex++;
        this.currentOffenseRecord = this.offense.offenseRecords[this.currentOffenseRecordIndex];
        this.updateFormControls();
        this.updateToCurrentOffenseRecord.emit(this.currentOffenseRecord);
        this.updateCanDeleteCurrentOffenseRecord.emit(true);
        this.offenseRecordFormGroup.markAsPristine();
      }
    }
  }

  async lastOffense() {
    if (!this.onMaxOffenseRecord && this.hasOffenses) {
      let save = true;
      if (this.offenseRecordFormGroup.dirty && await PopupHelper.saveUnsavedChangesHelper() === ConfirmResult.Cancel) {
        save = false;
      }

      if (save) {
        this.removeNewRecord();
        this.currentOffenseRecordIndex = this.getMaxOffenseRecords();
        this.currentOffenseRecord = this.offense.offenseRecords[this.currentOffenseRecordIndex];
        this.updateFormControls();
        this.updateToCurrentOffenseRecord.emit(this.currentOffenseRecord);
        this.updateCanDeleteCurrentOffenseRecord.emit(true);
        this.offenseRecordFormGroup.markAsPristine();
      }
    }
  }

  async addNewRecord(ignoreUnsavedChanges: boolean = false) {

    let save = true;
    if (!ignoreUnsavedChanges
      && this.offenseRecordFormGroup.dirty
      && await PopupHelper.saveUnsavedChangesHelper() === ConfirmResult.Cancel) {
      save = false;
    }

    if (save) {

      const maxOffenceNumber = this.getNewOffenseNumber();
      this.currentOffenseRecord = OffenseRecord.createNewOffenseRecord(this.requestNumber, maxOffenceNumber);
      this.offense.offenseRecords.push(this.currentOffenseRecord);
      this.currentOffenseRecordIndex = this.getMaxOffenseRecords();
      this.isNewRecord = true;
      this.updateFormControls();
      this.updateToCurrentOffenseRecord.emit(this.currentOffenseRecord);
      this.updateCanDeleteCurrentOffenseRecord.emit(false);
    }
  }

  setSentenceLevel(isUiCalling: boolean, changeControlState: boolean) {
    const hasProbation = this.formControl.hasValueByControlName('probation');
    const hasFines = this.formControl.hasValueByControlName('fines');
    const hasCourtCosts = this.formControl.hasValueByControlName('courtCosts');
    const hasJailTime = this.formControl.hasValueByControlName('jailTime');
    const hasPrisonTime = this.formControl.hasValueByControlName('prisonTime');
    const hasSuspensionTime = this.formControl.hasValueByControlName('suspensionTime');
    const hasNextCourtDate = this.formControl.hasValueByControlName('nextCourtDate');
    const hasOtherSentenceNotes = this.formControl.hasValueByControlName('otherSentenceNotes');

    const lockSentenceLevel = hasProbation || hasFines || hasCourtCosts || hasJailTime
      || hasPrisonTime || hasSuspensionTime || hasNextCourtDate || hasOtherSentenceNotes;

    if (!isUiCalling) {
      if (lockSentenceLevel) {
        this.formControl.setValueByControlName('sentenceLevel', SentenceLevel.Record.toString());
        this.isOffense = false;
      } else {
        this.formControl.setValueByControlName('sentenceLevel', SentenceLevel.Offense.toString());
        this.isOffense = true;
      }
    } else {
      const sentenceLevel = this.formControl.getNumberByControlName('sentenceLevel') as SentenceLevel;
      this.isOffense = sentenceLevel === SentenceLevel.Offense;
    }

    if (changeControlState) {
      if (this.isOffense) {
        this.formControl.disableControlByControlName('probation');
        this.formControl.disableControlByControlName('fines');
        this.formControl.disableControlByControlName('courtCosts');
        this.formControl.disableControlByControlName('jailTime');
        this.formControl.disableControlByControlName('prisonTime');
        this.formControl.disableControlByControlName('suspensionTime');
        this.formControl.disableControlByControlName('nextCourtDate');
        this.formControl.disableControlByControlName('otherSentenceNotes');
        this.formControl.enableControlByControlName('sentenceLevel');
      } else {
        this.formControl.enableControlByControlName('probation');
        this.formControl.enableControlByControlName('fines');
        this.formControl.enableControlByControlName('courtCosts');
        this.formControl.enableControlByControlName('jailTime');
        this.formControl.enableControlByControlName('prisonTime');
        this.formControl.enableControlByControlName('suspensionTime');
        this.formControl.enableControlByControlName('nextCourtDate');
        this.formControl.enableControlByControlName('otherSentenceNotes');
      }
    }

    if (lockSentenceLevel) {
      this.formControl.disableControlByControlName('sentenceLevel');
    } else {
      this.formControl.enableControlByControlName('sentenceLevel');
    }
  }

  updateToCurrentRecordCharges() {
    this.offenseRecordFormGroup.markAsDirty();
    this.updateToCurrentOffenseCharges.emit();
  }

  private removeNewRecord() {
    if (this.isNewRecord) {
      this.offense.offenseRecords.pop();
      this.isNewRecord = false;
    }
  }

  private handleRecordNavigationButtons() {
    this.firstButtonActive = true;
    this.previousButtonActive = true;
    this.nextButtonActive = true;
    this.lastButtonActive = true;

    if (this.currentOffenseRecordIndex === 0) {
      this.firstButtonActive = false;
      this.previousButtonActive = false;
    }

    if (this.offense.offenseRecords.length - 1 === this.currentOffenseRecordIndex) {
      this.nextButtonActive = false;
      this.lastButtonActive = false;
    }

    this.addNewButtonActive = !this.isNewRecord;
  }

  private validateDates() {
    const offenseDate = this.formControl.getDateByControlName('offenseDate');
    const fileDate = this.formControl.getDateByControlName('fileDate');
    const arrestDate = this.formControl.getDateByControlName('arrestDate');
    const dispositionDate = this.formControl.getDateByControlName('dispositionDate');
    const offenseChargeDates = this.currentOffenseRecord.charges
      .filter(c => c.offenseDate != null)
      .map(c => c.offenseDate);
    const fileChargeDates = this.currentOffenseRecord.charges
      .filter(c => c.fileDate != null)
      .map(c => c.fileDate);
    const arrestChargeDates = this.currentOffenseRecord.charges
      .filter(c => c.arrestDate != null)
      .map(c => c.arrestDate);
    const dispositionChargeDates = this.currentOffenseRecord.charges
      .filter(c => c.dispositionDate != null)
      .map(c => c.dispositionDate);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (offenseDate !== null && offenseDate > today) {
      this.formControl.setValidationErrorByControlName('offenseDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    } else {
      this.formControl.clearValidatorErrorByControlName('offenseDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    }

    if (fileDate !== null && fileDate > today) {
      this.formControl.setValidationErrorByControlName('fileDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    } else {
      this.formControl.clearValidatorErrorByControlName('fileDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    }

    if (arrestDate !== null && arrestDate > today) {
      this.formControl.setValidationErrorByControlName('arrestDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    } else {
      this.formControl.clearValidatorErrorByControlName('arrestDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    }

    if (dispositionDate !== null && dispositionDate > today) {
      this.formControl.setValidationErrorByControlName('dispositionDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    } else {
      this.formControl.clearValidatorErrorByControlName('dispositionDateGreaterThanCurrentDate', 'greaterThanCurrentDate');
    }

    if (offenseDate !== null && fileDate !== null && offenseDate > fileDate) {
      this.formControl.setValidationErrorByControlName('fileDateDateOlderThanOffenseDate', 'olderThan');
    } else {
      this.formControl.clearValidatorErrorByControlName('fileDateDateOlderThanOffenseDate', 'olderThan');
    }

    if (offenseDate !== null && dispositionDate !== null && offenseDate > dispositionDate) {
      this.formControl.setValidationErrorByControlName('dispositionDateOlderThanOffenseDate', 'olderThan');
    } else {
      this.formControl.clearValidatorErrorByControlName('dispositionDateOlderThanOffenseDate', 'olderThan');
    }

    if (fileDate !== null && dispositionDate !== null && fileDate > dispositionDate) {
      this.formControl.setValidationErrorByControlName('dispositionDateOlderThanFileDate', 'olderThan');
    } else {
      this.formControl.clearValidatorErrorByControlName('dispositionDateOlderThanFileDate', 'olderThan');
    }

    if (offenseDate !== null && !offenseChargeDates.some(c => c.getTime() === offenseDate.getTime())) {
      this.formControl.setValidationErrorByControlName('offenseDateAtRecordAndChargeLevel', 'noChargeLevel');
    } else {
      this.formControl.clearValidatorErrorByControlName('offenseDateAtRecordAndChargeLevel', 'noChargeLevel');
    }

    if (fileDate !== null && !fileChargeDates.some(c => c.getTime() === fileDate.getTime())) {
      this.formControl.setValidationErrorByControlName('fileDateAtRecordAndChargeLevel', 'noChargeLevel');
    } else {
      this.formControl.clearValidatorErrorByControlName('fileDateAtRecordAndChargeLevel', 'noChargeLevel');
    }

    if (arrestDate !== null && !arrestChargeDates.some(c => c.getTime() === arrestDate.getTime())) {
      this.formControl.setValidationErrorByControlName('arrestDateAtRecordAndChargeLevel', 'noChargeLevel');
    } else {
      this.formControl.clearValidatorErrorByControlName('arrestDateAtRecordAndChargeLevel', 'noChargeLevel');
    }

    if (dispositionDate !== null && !dispositionChargeDates.some(c => c.getTime() === dispositionDate.getTime())) {
      this.formControl.setValidationErrorByControlName('dispositionDateAtRecordAndChargeLevel', 'noChargeLevel');
    } else {
      this.formControl.clearValidatorErrorByControlName('dispositionDateAtRecordAndChargeLevel', 'noChargeLevel');
    }
  }

  validateIdentifier() {
    const hasNameIdentifier: boolean = this.formControl.getBooleanByControlName('hasNameIdentifier');
    const hasDobIdentifier: boolean = this.formControl.getBooleanByControlName('hasDobIdentifier');
    const hasSsnIdentifier: boolean = this.formControl.getBooleanByControlName('hasSsnIdentifier');
    const hasSinIdentifier: boolean = this.formControl.getBooleanByControlName('hasSinIdentifier');
    const hasAddressIdentifier: boolean = this.formControl.getBooleanByControlName('hasAddressIdentifier');
    const hasDlnIdentifier: boolean = this.formControl.getBooleanByControlName('hasDlnIdentifier');
    const dobType: string = this.formControl.getValueByControlName('dobType');
    const isHeightenedIdentifier: Boolean = this.offense.offenseSearch.isHeightenedIdentifier;
    let identifierCount = 0;

    if (hasNameIdentifier) {
      identifierCount++;
    }

    if (hasDobIdentifier) {
      identifierCount++;
    }

    if (hasSsnIdentifier) {
      identifierCount++;
    }

    if (hasSinIdentifier) {
      identifierCount++;
    }

    if (hasAddressIdentifier) {
      identifierCount++;
    }

    if (hasDlnIdentifier) {
      identifierCount++;
    }

    if ('no-dob' === dobType && hasDobIdentifier) {
      this.formControl.setValidationErrorByControlName('hasIdentifier', 'dobIdentifierWithDobNotListed');
    } else if (isHeightenedIdentifier && identifierCount < 1) {
      this.formControl.setValidationErrorByControlName('hasIdentifier', 'hasAdditionalIdentifier');
    } else if (!isHeightenedIdentifier && !hasNameIdentifier && !hasDobIdentifier
               && !hasSsnIdentifier && !hasSinIdentifier
               && !hasAddressIdentifier && !hasDlnIdentifier) {
      this.formControl.setValidationErrorByControlName('hasIdentifier', 'hasIdentifier');
    } else {
      this.formControl.clearValidatorErrorByControlName('hasIdentifier', 'hasIdentifier');
    }
  }

  validateDob() {
    const dobType = this.formControl.getValueByControlName('dobType');
    if ('full-dob' === dobType) {
      const month = this.formControl.getValueByControlName('month');
      const day = this.formControl.getValueByControlName('day');
      const year = this.formControl.getValueByControlName('year');
      const dob = new DateHandler(`${month}${day}${year}`, 1900);
      const monthAndDay = `${month}${day}`;

      if (monthAndDay.length !== 4 || !dob.isValid) {
        this.formControl.setValidationErrorByControlName('fullDobValidator', 'isFullDobDate');
      } else {
        this.formControl.setValueByControlName('year', dob.year);
        this.formControl.clearValidatorErrorByControlName('fullDobValidator', 'isFullDobDate');
      }
    } else if ('partial-dob' === dobType) {
      const month = this.formControl.getValueByControlName('month');
      const day = this.formControl.getValueByControlName('day');
      const year = this.formControl.getValueByControlName('year');

      if (((month.length > 0 && !month.match(/^-?\d+$/))
          || (day.length > 0 && !day.match(/^-?\d+$/))
          || (year.length > 0 && !year.match(/^-?\d+$/)))
          || month.length === 0 && day.length === 0 && year.length === 0) {
        this.formControl.setValidationErrorByControlName('partialDobValidator', 'isPartialDate');
      } else {
        this.formControl.clearValidatorErrorByControlName('partialDobValidator', 'isPartialDate');
      }
    }
    this.validateIdentifier();
  }

  checkIfDisableDob() {
    const dobType: string = this.formControl.getValueByControlName('dobType');
    if ('no-dob' === dobType) {
      this.formControl.setValueByControlName('month', '');
      this.formControl.setValueByControlName('day', '');
      this.formControl.setValueByControlName('year', '');
      this.formControl.getControlByControlName('month').disable();
      this.formControl.getControlByControlName('day').disable();
      this.formControl.getControlByControlName('year').disable();
      this.formControl.clearValidatorErrorByControlName('fullDobValidator', 'isFullDobDate');
      this.formControl.clearValidatorErrorByControlName('partialDobValidator', 'isPartialDate');
    } else if ('full-dob' === dobType) {
      this.formControl.getControlByControlName('month').enable();
      this.formControl.getControlByControlName('day').enable();
      this.formControl.getControlByControlName('year').enable();
      this.validateDob();
      this.formControl.clearValidatorErrorByControlName('partialDobValidator', 'isPartialDate');
      this.formControl.clearValidatorErrorByControlName('hasIdentifier', 'dobIdentifierWithDobNotListed');
    } else {
      this.formControl.getControlByControlName('month').enable();
      this.formControl.getControlByControlName('day').enable();
      this.formControl.getControlByControlName('year').enable();
      this.validateDob();
      this.formControl.clearValidatorErrorByControlName('fullDobValidator', 'isFullDobDate');
      this.formControl.clearValidatorErrorByControlName('hasIdentifier', 'dobIdentifierWithDobNotListed');
    }

    this.validateIdentifier();
  }

  updateOffenseDates(offenseDates: any) {
    this.formControl.setValueByControlName('offenseDate', offenseDates.offenseDate);
    this.formControl.setValueByControlName('fileDate',  offenseDates.fileDate);
    this.formControl.setValueByControlName('arrestDate',  offenseDates.arrestDate);
    this.formControl.setValueByControlName('dispositionDate',  offenseDates.dispositionDate);
    this.validateDates();
  }

  saveCompletedSuccessfully() {
    this.isNewRecord = false;
    this.handleRecordNavigationButtons();
    this.updateCurrentOffense();
    this.updateCanDeleteCurrentOffenseRecord.emit(true);
  }

  deleteSuccessfully() {
    if (this.offense.offenseRecords.length > 0) {
      this.firstOffense(true, true);
    } else {
      this.addNewRecord(true);
    }
  }

  setFocusForDobMismatch() {
    this.dobMonthElement.nativeElement.focus();
  }

  private updateFormControls() {

    this.formControl.setValueByControlName('hasNameIdentifier', this.currentOffenseRecord.hasNameIdentifier);
    this.formControl.setValueByControlName('hasDobIdentifier', this.currentOffenseRecord.hasDobIdentifier);
    this.formControl.setValueByControlName('hasSsnIdentifier', this.currentOffenseRecord.hasSsnIdentifier);
    this.formControl.setValueByControlName('hasSinIdentifier', this.currentOffenseRecord.hasSinIdentifier);
    this.formControl.setValueByControlName('hasAddressIdentifier', this.currentOffenseRecord.hasAddressIdentifier);
    this.formControl.setValueByControlName('hasDlnIdentifier', this.currentOffenseRecord.hasDlnIdentifier);
    this.validateIdentifier();

    this.formControl.setValueByControlName('hasFaxInformation', this.currentOffenseRecord.hasFaxInformation);
    this.formControl.setValueByControlName('courtRecordedFullName', this.currentOffenseRecord.courtRecordFullName);
    this.formControl.setValueByControlName('nameDiffers', this.currentOffenseRecord.nameDiffers);
    this.formControl.setValueByControlName('referenceNumber', this.currentOffenseRecord.referenceNumber);

    if (this.currentOffenseRecord.hasPartialDobIdentifier) {
      this.formControl.setValueByControlName('dobType', 'partial-dob');
      this.formControl.setValueByControlName('month', this.currentOffenseRecord.partialMonth);
      this.formControl.setValueByControlName('day', this.currentOffenseRecord.partialDay);
      this.formControl.setValueByControlName('year', this.currentOffenseRecord.partialYear);
    } else if (this.currentOffenseRecord.hasNoDobIdentifier) {
      this.formControl.setValueByControlName('dobType', 'no-dob');
    } else {
      this.formControl.setValueByControlName('dobType', 'full-dob');
      const dob = new DateHandler(this.currentOffenseRecord.courtRecordDob, 1900);
      this.formControl.setValueByControlName('month', dob.month);
      this.formControl.setValueByControlName('day', dob.day);
      this.formControl.setValueByControlName('year', dob.year);
    }

    this.checkIfDisableDob();

    this.formControl.setValueByControlName('offenseDate', this.convertToDateFormat(this.currentOffenseRecord.offenseDate, 2000));
    this.formControl.setValueByControlName('fileDate', this.convertToDateFormat(this.currentOffenseRecord.fileDate, 2000));
    this.formControl.setValueByControlName('arrestDate', this.convertToDateFormat(this.currentOffenseRecord.arrestDate, 2000));
    this.formControl.setValueByControlName('dispositionDate', this.convertToDateFormat(this.currentOffenseRecord.dispositionDate, 2000));
    this.validateDates();

    this.formControl.setValueByControlName('probation', this.currentOffenseRecord.probation);
    this.formControl.setValueByControlName('fines', this.currentOffenseRecord.fines);
    this.formControl.setValueByControlName('courtCosts', this.currentOffenseRecord.courtCosts);
    this.formControl.setValueByControlName('jailTime', this.currentOffenseRecord.jailTime);
    this.formControl.setValueByControlName('prisonTime', this.currentOffenseRecord.prisonTime);
    this.formControl.setValueByControlName('suspensionTime', this.currentOffenseRecord.suspensionTime);
    this.formControl.setValueByControlName('nextCourtDate', this.convertToDateFormat(this.currentOffenseRecord.nextCourtDate, 2000));
    this.formControl.setValueByControlName('comments', this.currentOffenseRecord.comments);
    this.formControl.setValueByControlName('otherSentenceNotes', this.currentOffenseRecord.otherSentenceNotes);

    this.handleRecordNavigationButtons();
    this.setSentenceLevel(false, true);
  }

  private updateCurrentOffense() {
    if (this.currentOffenseRecordIndex !== undefined) {
      this.currentOffenseRecord.hasNameIdentifier = this.formControl.getBooleanByControlName('hasNameIdentifier');
      this.currentOffenseRecord.hasDobIdentifier = this.formControl.getBooleanByControlName('hasDobIdentifier');
      this.currentOffenseRecord.hasSsnIdentifier = this.formControl.getBooleanByControlName('hasSsnIdentifier');
      this.currentOffenseRecord.hasSinIdentifier = this.formControl.getBooleanByControlName('hasSinIdentifier');
      this.currentOffenseRecord.hasAddressIdentifier = this.formControl.getBooleanByControlName('hasAddressIdentifier');
      this.currentOffenseRecord.hasDlnIdentifier = this.formControl.getBooleanByControlName('hasDlnIdentifier');

      this.currentOffenseRecord.hasFaxInformation = this.formControl.getBooleanByControlName('hasFaxInformation');
      this.currentOffenseRecord.courtRecordFullName = this.formControl.getValueByControlName('courtRecordedFullName');
      this.currentOffenseRecord.nameDiffers = this.formControl.getBooleanByControlName('nameDiffers');
      this.currentOffenseRecord.referenceNumber = this.formControl.getValueByControlName('referenceNumber');

      const dobType: string = this.formControl.getValueByControlName('dobType');
      this.currentOffenseRecord.hasNoDobIdentifier = 'no-dob' === dobType;
      this.currentOffenseRecord.hasPartialDobIdentifier = 'partial-dob' === dobType;
      if ('partial-dob' === dobType || 'full-dob' === dobType) {
        const month = this.formControl.getValueByControlName('month');
        const day = this.formControl.getValueByControlName('day');
        const year = this.formControl.getValueByControlName('year');

        if (this.currentOffenseRecord.hasPartialDobIdentifier) {
          this.currentOffenseRecord.partialMonth = month;
          this.currentOffenseRecord.partialDay = day;
          this.currentOffenseRecord.partialYear = year;
        } else {
          this.currentOffenseRecord.courtRecordDob = new Date(`${month}/${day}/${year}`);
        }
      }

      this.currentOffenseRecord.offenseDate = new Date(this.formControl.getValueByControlName('offenseDate'));
      this.currentOffenseRecord.fileDate = new Date(this.formControl.getValueByControlName('fileDate'));
      this.currentOffenseRecord.arrestDate = new Date(this.formControl.getValueByControlName('arrestDate'));
      this.currentOffenseRecord.dispositionDate = new Date(this.formControl.getValueByControlName('dispositionDate'));
      this.currentOffenseRecord.probation = this.formControl.getValueByControlName('probation');
      this.currentOffenseRecord.fines = this.formControl.getValueByControlName('fines');
      this.currentOffenseRecord.courtCosts = this.formControl.getValueByControlName('courtCosts');
      this.currentOffenseRecord.jailTime = this.formControl.getValueByControlName('jailTime');
      this.currentOffenseRecord.prisonTime = this.formControl.getValueByControlName('prisonTime');
      this.currentOffenseRecord.suspensionTime = this.formControl.getValueByControlName('suspensionTime');
      this.currentOffenseRecord.nextCourtDate = new Date(this.formControl.getValueByControlName('nextCourtDate'));
      this.currentOffenseRecord.comments = this.formControl.getValueByControlName('comments');
      this.currentOffenseRecord.otherSentenceNotes = this.formControl.getValueByControlName('otherSentenceNotes');
    }
  }

  private get onMinOffenseRecord(): boolean {
    return this.hasOffenses && this.currentOffenseRecordIndex === 0;
  }

  private get onMaxOffenseRecord(): boolean {
    return this.hasOffenses && this.currentOffenseRecordIndex >= this.offense.offenseRecords.length - 1;
  }

  private getMaxOffenseRecords(): number {
    return this.hasOffenses ? this.offense.offenseRecords.length - 1 : null;
  }

  private getNewOffenseNumber(): number {
    let offenseNumber = 1;
    if (this.hasOffenses && this.offense.offenseRecords.length > 0) {
      offenseNumber = Math.max(...this.offense.offenseRecords.map((or: OffenseRecord) => or.offenseNumber)) + 1;
    }

    return offenseNumber;
  }

  private get hasOffenses(): boolean {
    return this.offense !== null && this.offense !== undefined && this.offense !== null && this.offense.offenseRecords !== undefined;
  }

  private convertToDateFormat(date: any, baseCentury: number) {
    return new DateHandler(date, baseCentury).format();
  }

  get sentenceLevel() { return this.offenseRecordFormGroup.get('sentenceLevel'); }
  get hasIdentifier() { return this.offenseRecordFormGroup.get('hasIdentifier'); }
  get hasNameIdentifier() { return this.offenseRecordFormGroup.get('hasNameIdentifier'); }
  get hasDobIdentifier() { return this.offenseRecordFormGroup.get('hasDobIdentifier'); }
  get hasSsnIdentifier() { return this.offenseRecordFormGroup.get('hasSsnIdentifier'); }
  get hasSinIdentifier() { return this.offenseRecordFormGroup.get('hasSinIdentifier'); }
  get hasAddressIdentifier() { return this.offenseRecordFormGroup.get('hasAddressIdentifier'); }
  get hasDlnIdentifier() { return this.offenseRecordFormGroup.get('hasDlnIdentifier'); }
  get courtRecordedFullName() { return this.offenseRecordFormGroup.get('courtRecordedFullName'); }
  get nameDiffers() { return this.offenseRecordFormGroup.get('nameDiffers'); }
  get referenceNumber() { return this.offenseRecordFormGroup.get('referenceNumber'); }
  get dobType() { return this.offenseRecordFormGroup.get('dobType'); }
  get month() { return this.offenseRecordFormGroup.get('month'); }
  get day() { return this.offenseRecordFormGroup.get('day'); }
  get year() { return this.offenseRecordFormGroup.get('year'); }
  get fullDobValidator() { return this.offenseRecordFormGroup.get('fullDobValidator'); }
  get partialDobValidator() { return this.offenseRecordFormGroup.get('partialDobValidator'); }
  get offenseDate() { return this.offenseRecordFormGroup.get('offenseDate'); }
  get fileDate() { return this.offenseRecordFormGroup.get('fileDate'); }
  get arrestDate() { return this.offenseRecordFormGroup.get('arrestDate'); }
  get dispositionDate() { return this.offenseRecordFormGroup.get('dispositionDate'); }
  get hasRequiredDate() { return this.offenseRecordFormGroup.get('hasRequiredDate'); }
  get offenseDateGreaterThanCurrentDate() { return this.offenseRecordFormGroup.get('offenseDateGreaterThanCurrentDate'); }
  get fileDateGreaterThanCurrentDate() { return this.offenseRecordFormGroup.get('fileDateGreaterThanCurrentDate'); }
  get arrestDateGreaterThanCurrentDate() { return this.offenseRecordFormGroup.get('arrestDateGreaterThanCurrentDate'); }
  get dispositionDateGreaterThanCurrentDate() { return this.offenseRecordFormGroup.get('dispositionDateGreaterThanCurrentDate'); }
  get fileDateDateOlderThanOffenseDate() { return this.offenseRecordFormGroup.get('fileDateDateOlderThanOffenseDate'); }
  get dispositionDateOlderThanOffenseDate() { return this.offenseRecordFormGroup.get('dispositionDateOlderThanOffenseDate'); }
  get dispositionDateOlderThanFileDate() { return this.offenseRecordFormGroup.get('dispositionDateOlderThanFileDate'); }
  get offenseDateAtRecordAndChargeLevel() { return this.offenseRecordFormGroup.get('offenseDateAtRecordAndChargeLevel'); }
  get fileDateAtRecordAndChargeLevel() { return this.offenseRecordFormGroup.get('fileDateAtRecordAndChargeLevel'); }
  get arrestDateAtRecordAndChargeLevel() { return this.offenseRecordFormGroup.get('arrestDateAtRecordAndChargeLevel'); }
  get dispositionDateAtRecordAndChargeLevel() { return this.offenseRecordFormGroup.get('dispositionDateAtRecordAndChargeLevel'); }
  get probation() { return this.offenseRecordFormGroup.get('probation'); }
  get fines() { return this.offenseRecordFormGroup.get('fines'); }
  get courtCosts() { return this.offenseRecordFormGroup.get('courtCosts'); }
  get jailTime() { return this.offenseRecordFormGroup.get('jailTime'); }
  get prisonTime() { return this.offenseRecordFormGroup.get('prisonTime'); }
  get suspensionTime() { return this.offenseRecordFormGroup.get('suspensionTime'); }
  get nextCourtDate() { return this.offenseRecordFormGroup.get('nextCourtDate'); }
  get comments() { return this.offenseRecordFormGroup.get('comments'); }
  get otherSentenceNotes() { return this.offenseRecordFormGroup.get('otherSentenceNotes'); }


  public  currentOffenseRecord: OffenseRecord;
  public isOffense: boolean;
  public offenseRecordFormGroup: FormGroup;

  private offense: Offense;
  private currentOffenseRecordIndex: number = undefined;
  private isNewRecord = false;
  private firstButtonActive: boolean;
  private previousButtonActive: boolean;
  private nextButtonActive: boolean;
  private lastButtonActive: boolean;
  private addNewButtonActive: boolean;
  private formControl: FormControl;
  @ViewChild('dobMonth') dobMonthElement: ElementRef;
}



// WEBPACK FOOTER //
// ./src/app/offenseRecord/offenseRecord.component.ts